<script setup lang="ts">
import { useToast } from '@/composables/useToast';
import type { Toast, ToastAction } from '@/composables/useToast';
import { log } from '@/lib/log';
const { queue, dismiss } = useToast();

async function runAction(t: Toast, action: ToastAction | undefined = t.action) {
    if (!action) return;
    try {
        await action.run();
        if (action.dismissOnRun !== false) dismiss(t.id);
    } catch (err) {
        log.warn('toast.action_failed', { id: t.id, error: (err as Error).message });
    }
}
</script>

<template>
    <ol class="toast-stack" role="status" aria-live="polite">
        <li v-for="t in queue" :key="t.id" :class="['toast', t.type]">
            <span class="message">{{ t.message }}</span>
            <!-- Repeat counter (field sweep B6): the same message raised again
                 while it is on screen collapses into this entry instead of
                 stacking another card. -->
            <span v-if="t.count > 1" class="count" data-testid="toast-count">×{{ t.count }}</span>
            <button
                v-if="t.action"
                class="action"
                type="button"
                @click="runAction(t)"
            >
                {{ t.action.label }}
            </button>
            <button
                v-for="(extra, i) in t.actions ?? []"
                :key="`${t.id}-extra-${i}`"
                class="action"
                type="button"
                @click="runAction(t, extra)"
            >
                {{ extra.label }}
            </button>
            <button class="close" type="button" @click="dismiss(t.id)" aria-label="Dismiss">×</button>
        </li>
    </ol>
</template>

<style scoped>
.toast-stack {
    position: fixed;
    /* Yield to the persistent PIP (Atlas open-q #7): App.vue sets
       --pip-reserve-bottom to the px needed to clear the mini tile/bar; with no
       PIP it's 0, so the base offset wins. */
    bottom: max(
        calc(var(--space-lg) + var(--safe-area-inset-bottom)),
        var(--pip-reserve-bottom, 0px)
    );
    right: var(--space-lg);
    z-index: var(--z-toast);
    display: flex;
    flex-direction: column;
    gap: var(--space-sm);
    list-style: none;
    margin: 0;
    padding: 0;
    pointer-events: none;
}

.toast {
    pointer-events: auto;
    min-width: 240px;
    max-width: 360px;
    display: flex;
    align-items: flex-start;
    gap: var(--space-sm);
    padding: var(--space-md);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-lg);
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border);
    color: var(--color-text-primary);
    font-size: 13px;
    animation: toast-in 200ms ease forwards;
}

.toast.info {
    border-left: 4px solid var(--color-accent);
}

.toast.success {
    border-left: 4px solid var(--color-success);
}

.toast.warn {
    border-left: 4px solid var(--color-warning);
}

.toast.error {
    border-left: 4px solid var(--color-error);
}

.message {
    flex: 1;
}

.count {
    flex: 0 0 auto;
    align-self: center;
    padding: 0 var(--space-xs);
    border-radius: var(--radius-sm);
    background: var(--color-bg-primary);
    border: 1px solid var(--color-border-light);
    color: var(--color-text-muted);
    font-size: 11px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
}

.close {
    color: var(--color-text-muted);
    font-size: 18px;
    line-height: 1;
    padding: 0 var(--space-xs);
}

.action {
    flex: 0 0 auto;
    border: 1px solid var(--color-border-light);
    border-radius: var(--radius-sm);
    padding: var(--space-xs) var(--space-sm);
    color: var(--color-text-primary);
    background: var(--color-bg-primary);
    font-size: 12px;
    font-weight: 600;
}

.action:hover,
.action:focus-visible {
    border-color: var(--color-accent);
}

.close:hover,
.close:focus-visible {
    color: var(--color-text-primary);
}

@keyframes toast-in {
    from {
        opacity: 0;
        transform: translateY(8px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@media (prefers-reduced-motion: reduce) { /* feature: prefers-reduced-motion */
    .toast { animation: none; }
}
</style>
